import { useRef, useState, useMemo } from 'react'
import throttle from 'lodash/throttle'
import { trackInternalEvent } from '@rio/tracking'
import {
  actions as coRegActions,
  selectors as coRegSelectors,
} from 'builder/modules/coRegistration'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import templateHeightFactorSelector from 'builder/components/Helper/utils/templateHeightFactorSelector'
import { PreviewRefProps } from 'builder/components/PreviewPanel'
import { isResumeDocument } from 'builder/utils/getDocumentType'
import { DocumentCustomizationModalProps } from '../../types'

export const useDocumentPreview = (props: DocumentCustomizationModalProps) => {
  const { type, currentDocument, editorState } = props

  const isReadyToCoReg = useTypedSelector(coRegSelectors.isResumeReady)
  const isCoRegPopupOpen = useTypedSelector(coRegSelectors.isPopupOpen)
  const toggleCoRegPopup = coRegActions.togglePopup
  const openCoReg = coRegActions.open

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPagesNumber, setTotalPagesNumber] = useState<number>(1)
  const [isSheetVisible, setIsSheetVisible] = useState<boolean>(false)
  const [width, setWidth] = useState<number>(0)
  const [height, setHeight] = useState<number>(0)

  const contentRef = useRef<HTMLDivElement | null>(null)
  const previewBoxRef = useRef<HTMLDivElement | null>(null)
  const previewRef = useRef<PreviewRefProps>(null)
  const isDataLoaded = currentDocument && editorState.isLoaded

  const handlePreviewResize = throttle(() => {
    if (!previewBoxRef.current) return
    const width = previewBoxRef.current.offsetWidth
    const height = width / templateHeightFactorSelector(currentDocument)
    setWidth(width)
    setHeight(height)

    if (!previewRef.current) return
    previewRef.current.resize(width)
  }, 50)

  const isResume = isResumeDocument(currentDocument)

  const handlePaginationStateChange = (state: { currentPage: number; totalPagesCount: number }) => {
    setCurrentPage(state.currentPage + 1)
    setTotalPagesNumber(state.totalPagesCount)
  }

  const handlePreviousPageClick = () => {
    if (!previewRef.current) return
    previewRef.current.previousPage()
    trackInternalEvent(`change_${type}_preview_page`)
  }

  const handleNextPageClick = () => {
    if (!previewRef.current) return
    previewRef.current.nextPage()
    trackInternalEvent(`change_${type}_preview_page`)
  }

  const handleSheetOpen = () => {
    setIsSheetVisible(true)
  }

  const handleSheetClose = () => {
    setIsSheetVisible(false)
  }

  const templates = useMemo(() => {
    const { resumeTemplates, coverLetterTemplates } = editorState
    return isResume ? resumeTemplates : coverLetterTemplates
  }, [editorState, isResume])

  const currentTemplate = useMemo(() => {
    return templates.find(template => template.id === currentDocument.template)
  }, [currentDocument.template, templates])

  const colorSettings = useMemo(() => {
    if (currentTemplate) {
      const templateSettings = currentTemplate.settings
      let selectedColor = currentDocument ? currentDocument.color : null
      if (!selectedColor && templateSettings.color) selectedColor = templateSettings.color.default
      const colors = templateSettings.color ? templateSettings.color.values : []
      return { colors, selectedColor }
    }
    return { colors: [], selectedColor: null }
  }, [currentDocument, currentTemplate])

  return {
    isReadyToCoReg,
    isCoRegPopupOpen,
    toggleCoRegPopup,
    openCoReg,
    currentPage,
    totalPagesNumber,
    isSheetVisible,
    isDataLoaded,
    width,
    height,
    setCurrentPage,
    setTotalPagesNumber,
    setIsSheetVisible,
    contentRef,
    previewBoxRef,
    previewRef,
    handlePreviewResize,
    handlePaginationStateChange,
    handlePreviousPageClick,
    handleNextPageClick,
    handleSheetOpen,
    handleSheetClose,
    isResume,
    templates,
    currentTemplate,
    colorSettings,
  }
}
